import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { NgxGauge } from 'ngx-gauge';
import { GoalPanelType } from 'src/app/enums/goal-panel-type.enum';
import { GoalTypeEnum } from 'src/app/enums/goal-type.enum';
import { GoalPermissionsHelper } from 'src/app/helpers/goal-permissions.helper';
import { GoalCalculationData } from 'src/app/models/goal-calculation-data.model';
import { Goal } from 'src/app/models/goal.model';
import { GoalPeriod } from 'src/app/models/goalperiod.model';
import { ReviewPeriodAboutEmployeeResult } from 'src/app/models/review-period-aboutemployee-result.model';
import { ReviewPeriodCustomGoal } from 'src/app/models/review-period-custom-goal.model';
import { State } from 'src/app/models/state.model';
import { StateService } from 'src/app/services';
import { ResponsiveGaugeDirective } from 'src/app/shared/directives';
import { GaugeSettings } from 'src/app/shared/models';

@Component({
  selector: 'app-goal-panel',
  templateUrl: './goal-panel.component.html',
  styleUrl: './goal-panel.component.scss'
})
export class GoalPanelComponent implements AfterViewInit {

  @Input() 
  public title: string = '';

  @Input()
  public subtitle: string = '';

  @Input()
  public panelType: GoalPanelType;

  @Input()
  public goalPeriod: GoalPeriod;

  @Input()
  public reviewPeriodCustomGoal: ReviewPeriodCustomGoal;

  public goalPanelTypes = GoalPanelType;

  @ViewChild(ResponsiveGaugeDirective) gaugeContainerDirective?: ResponsiveGaugeDirective;
  @ViewChild('itemGauge') itemGauge?: NgxGauge;

  public state: State;

  public gaugeConfig: GaugeSettings = {
      breakpoints: [
          { maxWidth: 650, size: 55 },
          { maxWidth: 850, size: 65 },
          { maxWidth: 1150, size: 85 },
          { maxWidth: Infinity, size: 105 },
      ]
  };

  public get panelResult(): number | null {
    if (this.goalPeriod == null) return null;

    if (this.panelType === GoalPanelType.TextBasedGoals || this.panelType === GoalPanelType.NumericGoals) {
      let items: Goal[] = [];
      
      if (this.panelType === GoalPanelType.TextBasedGoals) {
        items = this.goalPeriod.textBasedGoals;
      } else if (this.panelType === GoalPanelType.NumericGoals) {
        items = this.goalPeriod.numericGoals;
      }

      return this.getGoalResult(items);
    }
    else if (this.panelType === GoalPanelType.CompetencyResult) {
      return this.getPanoramaResults(this.goalPeriod.panoramaResults);
    }

  }

  constructor(private router: Router, stateService: StateService) {
    this.state = stateService.state;
  }

  ngAfterViewInit(): void {
    if (this.itemGauge && this.gaugeContainerDirective) {
      this.gaugeContainerDirective.gauge = this.itemGauge;
      this.gaugeContainerDirective.debounceUpdate(); // az első méret frissítés
    }
  }

  public getGoalResult(items: Goal[]): number | null {
    if (this.goalPeriod == null || items == null || items.length === 0) return null;

    let calcParams: GoalCalculationData = { score: 0, divisor: 0 };
    let weightedCalc = items.some(x => x.weight && x.weight > 0);
    
    for (const item of items) {
      this.calculateWeightedResult(weightedCalc, item.weight, item.result, calcParams);
    }

    return calcParams.divisor > 0 ? calcParams.score / calcParams.divisor : null;
  }

  public calculateWeightedResult(weightedCalc: boolean, weight: number | null, val: number | null, calcParams: GoalCalculationData): number {
    if (weightedCalc)
    {
        if (!val || !weight)
            return;
        
        calcParams.score += val * weight;
        calcParams.divisor += weight;
    }
    else
    {
        if (!val) return;

        calcParams.score += val;
        calcParams.divisor++;
    }
  }

  public getPanoramaResults(items: ReviewPeriodAboutEmployeeResult[]): number {
    if (this.goalPeriod == null || items == null || items.length === 0) return 0;

    const resultId = this.goalPeriod.reviewPeriodAboutEmployeeResultsId;

    if (resultId == null) return 0;

    const selectedItem = items.find(x => x.id === resultId);

    if (!selectedItem) return 0;

    return selectedItem.result * 100;
  }

  public getGoalItems(goalType: GoalPanelType): Goal[] {
    if (this.goalPeriod == null) return [];

    switch (goalType) {
      case GoalPanelType.TextBasedGoals:
        return this.goalPeriod.textBasedGoals;
      case GoalPanelType.NumericGoals:
        return this.goalPeriod.numericGoals;
      
      default:
        return [];
    }
  }

  public get hasIndicators(): boolean {
    if (this.panelType == null) return false;
    
    return [GoalPanelType.TextBasedGoals, GoalPanelType.NumericGoals, GoalPanelType.CompetencyResult].includes(this.panelType);
  }

  public get isGoalItems(): boolean {
    return [GoalPanelType.TextBasedGoals, GoalPanelType.NumericGoals, GoalPanelType.GroupGoals].includes(this.panelType);
  }  

  public get panelWeight(): number {
    if (this.goalPeriod == null) return 0;

    switch (this.panelType) {
      case GoalPanelType.TextBasedGoals:
        return this.goalPeriod.textBasedCustomWeight;
      case GoalPanelType.NumericGoals:
        return this.goalPeriod.numericCustomWeight;
      case GoalPanelType.CompetencyResult:
        return this.goalPeriod.competencyWeight;
      default:
        return 0;
    }

  }

  public get items() {
    if (this.goalPeriod == null) return [];

    switch (this.panelType) {
      case GoalPanelType.TextBasedGoals:
        return this.goalPeriod.textBasedGoals;
      case GoalPanelType.NumericGoals:
        return this.goalPeriod.numericGoals;
      case GoalPanelType.CompetencyResult:
        return this.goalPeriod.panoramaResults;
      case GoalPanelType.GroupGoals:
        return this.goalPeriod.groupGoals;
      case GoalPanelType.CustomGoals:
        return this.reviewPeriodCustomGoal && this.reviewPeriodCustomGoal.customGoal ? this.reviewPeriodCustomGoal.customGoal.comments : [];
      default:
        return [];
    }
  }

  public allowNew(): boolean {
    return GoalPermissionsHelper.canGoalAddNew(this.goalPeriod, this.state);
  }

  public allowEditGroupGoals(): boolean {
   return GoalPermissionsHelper.canGroupGoalsEdit(this.goalPeriod, this.state);
  }

  // public onNewGoal() {
  //   if (this.panelType === GoalPanelType.TextBasedGoals) {
  //     this.router.navigate(['/add-text-based-goal', this.goalPeriod.id]);
  //   }
  //   else if (this.panelType === GoalPanelType.NumericGoals) {
  //     this.router.navigate(['/add-numeric-goal', this.goalPeriod.id]);
  //   }
    
  // }

  // public onGoalDelete(item: Goal, index: number) {
  //   if (item.goalType == GoalTypeEnum.TextBasedGoal)
  //   {
  //     this.goalPeriod.textBasedGoals.splice(index, 1);
  //     this.goalPeriod.textBasedGoals = [...this.goalPeriod.textBasedGoals]; // trigger change detection
  //   }
  //   else if (item.goalType == GoalTypeEnum.NumericGoal)
  //   {
  //     this.goalPeriod.numericGoals.splice(index, 1);
  //     this.goalPeriod.numericGoals = [...this.goalPeriod.numericGoals]; // trigger change detection
  //   }
  // }

  public onDeleteGroupGoal(groupGoalId: number, index: number) {
    this.goalPeriod.groupGoals.splice(index, 1);
    this.goalPeriod.groupGoals = [...this.goalPeriod.groupGoals]; // trigger change detection
  }

  public onEditGroupGoals() {
    this.router.navigate(['/group-goals', this.goalPeriod.id]);
  }

  public getResultStartDate(): any {
    if (this.goalPeriod == null || !this.goalPeriod?.reviewPeriodAboutEmployeeResultsId) return null;

    const selectedItem = this.goalPeriod.panoramaResults.find(x => x.id === this.goalPeriod.reviewPeriodAboutEmployeeResultsId);
    return selectedItem ? selectedItem.startDate : null;
  }

  // return Temporal.PlainDate
  public getResultEndDate(): any {
    if (this.goalPeriod == null || !this.goalPeriod?.reviewPeriodAboutEmployeeResultsId) return null;

    const selectedItem = this.goalPeriod.panoramaResults.find(x => x.id === this.goalPeriod.reviewPeriodAboutEmployeeResultsId);
    return selectedItem ? selectedItem.endDate : null;
  }

}
